import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const CLOSE_DELAY = 100;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {number | null} */
  #closeTimer = null;

  /** @type {State} */
  #state = {
    activeItem: null,
  };

  /** @type {string} Pointer type of the most recent pointerdown inside the menu. */
  #lastPointerType = '';

  connectedCallback() {
    super.connectedCallback();

    const signal = this.#abortController.signal;

    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.addEventListener('pointerenter', this.#cancelClose, { signal });
    this.addEventListener('submenu-resize', this.#handleSubmenuResize, { signal });
    this.addEventListener('pointerdown', this.#trackPointerType, { signal, capture: true });
    this.addEventListener('click', this.#handleTouchClick, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearCloseTimer();
  }

  /**
   * Time to allow for a closing animation.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu shadow DOM element.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Activate a menu item. Called from on:pointerenter and on:focus on each <li>.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Touch taps fire pointerenter on touch-down and pointerleave on lift,
    // which made submenus flash open and close on iPad. Touch opening is
    // handled by the click handler instead.
    if (event instanceof PointerEvent && event.pointerType === 'touch') return;
    this.#clearCloseTimer();
    this.#activateItem(event);
  };

  /**
   * Deactivate the menu. Called from on:pointerleave and on:blur on each <li>.
   * Only acts on blur (keyboard nav) — pointer-based closing is handled at the component level.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (event instanceof PointerEvent) return;
    this.#scheduleClose();
  }

  /**
   * Activate the given menu item immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  #activateItem = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Items without a submenu (e.g. plain top-level links) should not open the
    // mega menu or trigger the backdrop. Close any currently open menu instead.
    if (!submenu) {
      this.#close();
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    submenu.dataset.active = '';

    this.dataset.submenuType = 'card';

    this.#positionSubmenu(item, submenu);

    const submenuHeight = getSubmenuHeight(submenu);
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Position the submenu relative to its parent link:
   * - Constrain the hover bridge to the parent link's horizontal span so it
   *   doesn't overlap sibling nav items and trap the pointer.
   * - Horizontally center compact flat submenu cards under their parent link.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #positionSubmenu(item, submenu) {
    const linkRect = item.getBoundingClientRect();
    const subRect = submenu.getBoundingClientRect();

    submenu.style.setProperty('--bridge-left', `${linkRect.left - subRect.left}px`);
    submenu.style.setProperty('--bridge-width', `${linkRect.width}px`);

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (!inner || !isCompactFlatSubmenu(submenu)) return;

    const center = linkRect.left + linkRect.width / 2 - subRect.left;
    inner.style.setProperty('--submenu-card-left', `${center}px`);
  }

  /** @param {Event} event */
  #handlePointerLeave = (event) => {
    if (event instanceof PointerEvent && event.pointerType === 'touch') return;
    this.#scheduleClose();
  };

  /** @param {PointerEvent} event */
  #trackPointerType = (event) => {
    this.#lastPointerType = event.pointerType;
  };

  /**
   * On touch, the first tap on a parent item opens its submenu instead of
   * navigating; a second tap (or a tap on a submenu link) navigates.
   * @param {MouseEvent} event
   */
  #handleTouchClick = (event) => {
    if (this.#lastPointerType !== 'touch') return;
    if (!(event.target instanceof Element)) return;

    // Taps inside an open submenu should navigate normally.
    if (event.target.closest('.menu-list__submenu')) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem || !this.contains(listItem)) return;

    const item = findMenuItem(listItem);
    if (!item) return;

    const isDefaultSlot = listItem.slot === '';
    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Plain top-level links without a submenu navigate on the first tap.
    if (!submenu) return;

    // Second tap on the already-open item: let the link navigate.
    if (item === this.#state.activeItem) return;

    event.preventDefault();
    this.#clearCloseTimer();
    this.#activateItem(/** @type {any} */ ({ target: listItem }));
  };

  /**
   * Close the menu when a touch lands outside it.
   * @param {PointerEvent} event
   */
  #handleOutsidePointerDown = (event) => {
    if (event.pointerType !== 'touch') return;
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#clearCloseTimer();
    this.#close();
  };

  /** Cancel the close timer on any pointer re-entry. */
  #cancelClose = () => {
    this.#clearCloseTimer();
  };

  #scheduleClose = () => {
    this.#clearCloseTimer();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      this.#close();
    }, CLOSE_DELAY);
  };

  #clearCloseTimer = () => {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  #close = () => {
    const item = this.#state.activeItem;
    if (!item) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    delete this.dataset.submenuType;

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const cleanupDelay = Math.max(0, this.animationDelay);
    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, cleanupDelay);
  };

  /**
   * Recalculate --submenu-height when a sidebar panel changes.
   * @param {Event} _event
   */
  #handleSubmenuResize = (_event) => {
    const item = this.#state.activeItem;
    if (!item) return;

    let submenu = findSubmenu(item);
    if (!submenu) {
      const isOverflow = item.closest('[slot="overflow"]');
      if (isOverflow) submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#positionSubmenu(item, submenu);
      const submenuHeight = getSubmenuHeight(submenu);
      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {HTMLElement} submenu
 * @returns {boolean}
 */
function isCompactFlatSubmenu(submenu) {
  const inner = submenu.querySelector('.menu-list__submenu-inner');
  if (!inner) return false;

  return !inner.querySelector('.mega-menu-sidebar, .mega-menu__content, .mega-menu__link-image');
}

/**
 * @param {HTMLElement} submenu
 * @returns {number}
 */
function getSubmenuHeight(submenu) {
  if (isCompactFlatSubmenu(submenu)) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (inner) return inner.offsetHeight;
  }

  return submenu.offsetHeight;
}
